/**
 * Paying in Apple Pay's or Google Pay's sheet: a plan upgrade (plan checkout) or the domain cart
 * (domain checkout). The sheet shows the amount and the person approves it there: the portal starts a
 * PaymentIntent (`start`), Stripe.js confirms it — which only authorises it — and the portal then
 * checks it, keeps the card, takes the payment and delivers what was bought (`finish`). A payment the
 * portal never finishes is never taken; its authorisation lapses.
 */
import { useEffect, useRef } from "react";

import { paymentOptions, type Wallet } from "@/lib/stripe";

import { useWalletButton, WALLET_NAMES, type ApplePayRecurring, type WalletEntry } from "./useWalletButton";

export function useWalletPayment<Paid>(
  orgId: string,
  wallet: Wallet,
  {
    amountCents,
    recurring,
    start,
    finish,
    onPaid,
  }: {
    /** What the sheet shows: today's total, VAT included, in pence. */
    amountCents: number;
    recurring: ApplePayRecurring | null;
    /** The payment to have approved (the API's `/…/checkout/wallet`). */
    start: () => Promise<{ payment_reference: string; client_secret: string | null }>;
    /** Takes the approved payment and delivers the purchase. */
    finish: (reference: string) => Promise<Paid>;
    onPaid: (result: Paid) => void;
  },
): WalletEntry {
  // Read when the person approves, so new callbacks don't redraw the wallet's button.
  const calls = useRef({ start, finish, onPaid });
  useEffect(() => {
    calls.current = { start, finish, onPaid };
  });
  const name = WALLET_NAMES[wallet];
  return useWalletButton(orgId, wallet, {
    recurring,
    elementsOptions: paymentOptions(amountCents),
    incomplete: `${name} didn't finish. Nothing was charged.`,
    flow: {
      start: async () => {
        const started = await calls.current.start();
        if (!started.client_secret) throw new Error(`${name} isn't available right now.`);
        return started;
      },
      confirm: async (stripe, elements, started) => {
        const result = await stripe.confirmPayment({
          elements,
          clientSecret: started.client_secret!,
          redirect: "if_required",
          confirmParams: { return_url: window.location.href },
        });
        if (result.error || !result.paymentIntent)
          throw new Error(result.error?.message ?? `${name} didn't finish. Nothing was charged.`);
        // Authorised, waiting for the portal to take it.
        if (result.paymentIntent.status !== "requires_capture")
          throw new Error("Your bank did not approve the payment. Try again, or pay by card instead.");
        return started.payment_reference;
      },
      // Takes the payment and delivers the purchase. If this fails after the person approved, "Try
      // again" finishes the same payment instead of asking them to approve another one.
      finish: async (reference) => {
        calls.current.onPaid(await calls.current.finish(reference));
      },
    },
  });
}
